import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import SvgIcon, { type SvgIconProps } from '@mui/material/SvgIcon'

/**
 * Figma `dashboard-line 1` (5106:7559): frame **20×20**, vector in `inset-[12.5%]`
 * → inner glyph area **15×15** (75% of frame). Same slot for all sidebar line icons.
 */
const NAV_ICON_SLOT_PX = 20
const NAV_ICON_GLYPH_PX = 17

const navGlyphSx: SvgIconProps['sx'] = {
  fontSize: NAV_ICON_GLYPH_PX,
  width: NAV_ICON_GLYPH_PX,
  height: NAV_ICON_GLYPH_PX,
  flexShrink: 0,
  display: 'block',
}

function NavSvg(props: SvgIconProps & { viewBox: string; children: ReactNode }) {
  const { viewBox, children, sx, ...rest } = props
  const sxMerged: SvgIconProps['sx'] =
    sx == null ? navGlyphSx : [navGlyphSx, ...(Array.isArray(sx) ? sx : [sx])]
  return (
    <Box
      aria-hidden
      sx={{
        width: NAV_ICON_SLOT_PX,
        height: NAV_ICON_SLOT_PX,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <SvgIcon viewBox={viewBox} sx={sxMerged} {...rest}>
        {children}
      </SvgIcon>
    </Box>
  )
}

export function NavIconDashboard(props: SvgIconProps) {
  return (
    <NavSvg viewBox="0 0 15 15" {...props}>
      <path
        fill="currentColor"
        d="M9.16667 15C8.70642 15 8.33333 14.6269 8.33333 14.1667V7.5C8.33333 7.03975 8.70642 6.66667 9.16667 6.66667H14.1667C14.6269 6.66667 15 7.03975 15 7.5V14.1667C15 14.6269 14.6269 15 14.1667 15H9.16667ZM0.833333 8.33333C0.3731 8.33333 0 7.96025 0 7.5V0.833333C0 0.3731 0.3731 0 0.833333 0H5.83333C6.29358 0 6.66667 0.3731 6.66667 0.833333V7.5C6.66667 7.96025 6.29358 8.33333 5.83333 8.33333H0.833333ZM5 6.66667V1.66667H1.66667V6.66667H5ZM0.833333 15C0.3731 15 0 14.6269 0 14.1667V10.8333C0 10.3731 0.3731 10 0.833333 10H5.83333C6.29358 10 6.66667 10.3731 6.66667 10.8333V14.1667C6.66667 14.6269 6.29358 15 5.83333 15H0.833333ZM1.66667 13.3333H5V11.6667H1.66667V13.3333ZM10 13.3333H13.3333V8.33333H10V13.3333ZM8.33333 0.833333C8.33333 0.3731 8.70642 0 9.16667 0H14.1667C14.6269 0 15 0.3731 15 0.833333V4.16667C15 4.6269 14.6269 5 14.1667 5H9.16667C8.70642 5 8.33333 4.6269 8.33333 4.16667V0.833333ZM10 1.66667V3.33333H13.3333V1.66667H10Z"
      />
    </NavSvg>
  )
}

export function NavIconMegaphone(props: SvgIconProps) {
  return (
    <NavSvg viewBox="0 0 16.25 15.8333" {...props}>
      <path
        fill="currentColor"
        d="M5 11.6667C5 11.6667 10.8333 12.5 13.3333 15H14.1667C14.6269 15 15 14.6269 15 14.1667V9.11417C15.7188 8.92917 16.25 8.27658 16.25 7.5C16.25 6.72342 15.7188 6.07083 15 5.88583V0.833333C15 0.3731 14.6269 0 14.1667 0H13.3333C10.8333 2.5 5 3.33333 5 3.33333H1.66667C0.746192 3.33333 0 4.07952 0 5V10C0 10.9205 0.746192 11.6667 1.66667 11.6667H2.5L3.33333 15.8333H5V11.6667ZM6.66667 4.71767C7.23608 4.5955 7.93958 4.42661 8.69942 4.20311C10.0979 3.79178 11.875 3.14385 13.3333 2.14548V12.8545C11.875 11.8562 10.0979 11.2082 8.69942 10.7969C7.93958 10.5734 7.23608 10.4045 6.66667 10.2823V4.71767ZM1.66667 5H5V10H1.66667V5Z"
      />
    </NavSvg>
  )
}

export function NavIconBookOpen(props: SvgIconProps) {
  return (
    <NavSvg viewBox="0 0 16.6667 16.6667" {...props}>
      <path
        fill="currentColor"
        d="M9.16667 15V16.6667H7.5V15H0.833333C0.3731 15 0 14.6269 0 14.1667V0.833333C0 0.3731 0.3731 0 0.833333 0H5.83333C6.82892 0 7.72258 0.436458 8.33333 1.12848C8.94408 0.436458 9.83775 0 10.8333 0H15.8333C16.2936 0 16.6667 0.3731 16.6667 0.833333V14.1667C16.6667 14.6269 16.2936 15 15.8333 15H9.16667ZM15 13.3333V1.66667H10.8333C9.91283 1.66667 9.16667 2.41286 9.16667 3.33333V13.3333H15ZM7.5 13.3333V3.33333C7.5 2.41286 6.75383 1.66667 5.83333 1.66667H1.66667V13.3333H7.5Z"
      />
    </NavSvg>
  )
}

export function NavIconUser3(props: SvgIconProps) {
  return (
    <NavSvg viewBox="0 0 13.3333 17.5" {...props}>
      <path
        fill="currentColor"
        d="M13.3333 17.5H11.6667V15.8333C11.6667 14.4526 10.5474 13.3333 9.16667 13.3333H4.16667C2.78596 13.3333 1.66667 14.4526 1.66667 15.8333V17.5H0V15.8333C0 13.5322 1.86548 11.6667 4.16667 11.6667H9.16667C11.4678 11.6667 13.3333 13.5322 13.3333 15.8333V17.5ZM6.66667 10C3.90524 10 1.66667 7.76142 1.66667 5C1.66667 2.23857 3.90524 0 6.66667 0C9.42808 0 11.6667 2.23857 11.6667 5C11.6667 7.76142 9.42808 10 6.66667 10ZM6.66667 8.33333C8.50758 8.33333 10 6.84095 10 5C10 3.15905 8.50758 1.66667 6.66667 1.66667C4.82572 1.66667 3.33333 3.15905 3.33333 5C3.33333 6.84095 4.82572 8.33333 6.66667 8.33333Z"
      />
    </NavSvg>
  )
}

/** Remix `building-line` — outline style aligned with other nav glyphs */
export function NavIconBuilding(props: SvgIconProps) {
  return (
    <NavSvg viewBox="0 0 24 24" {...props}>
      <path
        fill="currentColor"
        d="M21 19H23V21H1V19H3V4C3 3.44772 3.44772 3 4 3H14C14.5523 3 15 3.44772 15 4V19H19V11H17V9H20C20.5523 9 21 9.44772 21 10V19ZM5 5V19H13V5H5ZM7 11H11V13H7V11ZM7 7H11V9H7V7Z"
      />
    </NavSvg>
  )
}

/** Remix `group-line` — Platform Admin Signal Providers nav (Figma `20:1036`). */
export function NavIconGroup(props: SvgIconProps) {
  return (
    <NavSvg viewBox="0 0 24 24" {...props}>
      <path
        fill="currentColor"
        d="M2 22C2 17.5817 5.58172 14 10 14C14.4183 14 18 17.5817 18 22H16C16 18.6863 13.3137 16 10 16C6.68629 16 4 18.6863 4 22H2ZM10 13C6.685 13 4 10.315 4 7C4 3.685 6.685 1 10 1C13.315 1 16 3.685 16 7C16 10.315 13.315 13 10 13ZM10 11C12.21 11 14 9.21 14 7C14 4.79 12.21 3 10 3C7.79 3 6 4.79 6 7C6 9.21 7.79 11 10 11ZM18.2837 14.7028C21.0644 15.9561 23 18.752 23 22H21C21 19.564 19.5483 17.4671 17.4628 16.5271L18.2837 14.7028ZM17.5962 3.41321C19.5944 4.23703 21 6.20361 21 8.5C21 11.3702 18.8042 13.7252 16 13.9776V11.9646C17.6967 11.7222 19 10.264 19 8.5C19 7.11935 18.2016 5.92603 17.041 5.35635L17.5962 3.41321Z"
      />
    </NavSvg>
  )
}

/** Remix `notification-2-line` — Platform Admin Notification Management nav (Figma `20:1040`). */
export function NavIconNotification2(props: SvgIconProps) {
  return (
    <NavSvg viewBox="0 0 24 24" {...props}>
      <path
        fill="currentColor"
        d="M22 20H2V18H3V11.0314C3 6.04348 7.02944 2 12 2C16.9706 2 21 6.04348 21 11.0314V18H22V20ZM5 18H19V11.0314C19 7.14806 15.866 4 12 4C8.13401 4 5 7.14806 5 11.0314V18ZM9.5 21H14.5C14.5 22.3807 13.3807 23.5 12 23.5C10.6193 23.5 9.5 22.3807 9.5 21Z"
      />
    </NavSvg>
  )
}

/** Remix `layout-3-line` — Platform Admin Template Library nav (Figma `20:1044`). */
export function NavIconLayout3(props: SvgIconProps) {
  return (
    <NavSvg viewBox="0 0 24 24" {...props}>
      <path
        fill="currentColor"
        d="M4 21C3.44772 21 3 20.5523 3 20V4C3 3.44772 3.44772 3 4 3H20C20.5523 3 21 3.44772 21 4V20C21 20.5523 20.5523 21 20 21H4ZM8 10H5V19H8V10ZM19 10H10V19H19V10ZM19 5H5V8H19V5Z"
      />
    </NavSvg>
  )
}

/** Figma Reports nav `6216:14565` — Remix `file-chart-line` (document + bars). */
export function NavIconFileChart(props: SvgIconProps) {
  return (
    <NavSvg viewBox="0 0 15 16.6667" {...props}>
      <path
        fill="currentColor"
        d="M6.66667 4.16667H8.33333V12.5H6.66667V4.16667ZM10 7.5H11.6667V12.5H10V7.5ZM3.33333 9.16667H5V12.5H3.33333V9.16667ZM10 1.66667H1.66667V15H13.3333V5H10V1.66667ZM0 0.8265C0 0.370042 0.372908 0 0.832083 0H10.8333L14.9998 4.16667L15 15.8271C15 16.2907 14.6292 16.6667 14.1722 16.6667H0.827833C0.370633 16.6667 0 16.2872 0 15.8402V0.8265Z"
      />
    </NavSvg>
  )
}
